import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

export const DISCIPLINES = ["mechanical", "electrical", "instrumentation", "piping", "civil/structural", "telecoms", "HVAC", "process"];
export const STAGES = ["A", "B", "C"];

export const fmtDate = (d) => (d ? new Date(d.length <= 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

export function useStageNames() {
  const { me } = useAuth();
  return me?.orgs?.find((o) => o.id === me.active_org_id)?.stage_names || { A: "Mechanical Completion", B: "Pre-commissioning", C: "Commissioning" };
}

const RAG = { red: "bg-red-500/15 text-red-400 border-red-500/40", amber: "bg-amber-500/15 text-amber-400 border-amber-500/40", green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" };

export const RagBadge = ({ rag, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 border font-mono text-[11px] uppercase tracking-wider ${RAG[rag] || RAG.green}`}>
    <span className={`w-1.5 h-1.5 ${rag === "red" ? "bg-red-400" : rag === "amber" ? "bg-amber-400" : "bg-emerald-400"}`} />{rag}
  </span>
);

export const Variance = ({ days }) => {
  if (days === null || days === undefined) return <span className="num text-slate-500">-</span>;
  const c = days > 7 ? "text-red-400" : days > 0 ? "text-amber-400" : "text-emerald-400";
  return <span className={`num font-semibold ${c}`}>{days > 0 ? `+${days}` : days} d</span>;
};

export const PctBar = ({ pct, className = "" }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="flex-1 h-1.5 bg-[#1E293B] min-w-[48px]"><div className="h-full bg-amber-500 transition-[width] duration-500" style={{ width: `${pct ?? 0}%` }} /></div>
    <span className="num text-xs text-slate-300 w-11 text-right">{pct === null || pct === undefined ? "-" : `${Math.round(pct)}%`}</span>
  </div>
);

export const Stat = ({ label, value, sub, testid, accent }) => (
  <div className="panel p-4" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`num text-2xl font-semibold mt-1 ${accent || "text-slate-100"}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export const Pick = ({ value, onChange, options, placeholder, testid, className = "", allLabel }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testid} className={`bg-[#0F172A] h-9 text-sm ${className}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {allLabel && <SelectItem value="all">{allLabel}</SelectItem>}
      {options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <SelectItem key={v} value={v}>{l}</SelectItem>; })}
    </SelectContent>
  </Select>
);

export const StatusPill = ({ status }) => {
  const c = { complete: "text-emerald-400 border-emerald-500/40", closed: "text-emerald-400 border-emerald-500/40", in_progress: "text-amber-400 border-amber-500/40", open: "text-slate-300 border-[#334155]" }[status] || "text-slate-300 border-[#334155]";
  return <span className={`px-2 py-0.5 border font-mono text-[10px] uppercase tracking-wider whitespace-nowrap ${c}`}>{status?.replace("_", " ")}</span>;
};

export const PageTitle = ({ eyebrow, title, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
    <div>
      {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-50">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Btn = ({ children, variant = "accent", className = "", ...rest }) => (
  <button {...rest} className={`px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${variant === "accent" ? "btn-accent" : "border border-[#334155] text-slate-200 hover:border-amber-500"} ${className}`}>{children}</button>
);

export const Loading = () => <div className="py-20 text-center font-mono text-xs text-slate-500" data-testid="loading">Loading…</div>;
